import { useEffect, useRef, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Download, Save, CheckCircle2, Layers, Copy, ImagePlus, Loader2, ExternalLink } from "lucide-react";
import { projectsApi, materialsApi } from "@/lib/api";
import {
  structItemMetrics, structTotals, structPriced, DEFAULT_STRUCTURAL,
  fmt, fmtMoney, MANUAL_GROUPS,
} from "@/lib/calc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const newItem = () => ({ id: crypto.randomUUID(), width: "", height: "", length: "", qty: 1 });
const UNIT_MAP = { "m²": "m2", "m³": "m3" };

export default function StructuralEditor({ id, initialProject }) {
  const nav = useNavigate();
  const [project, setProject] = useState(() => ({
    ...initialProject,
    structural: { ...DEFAULT_STRUCTURAL, ...(initialProject.structural || {}) },
  }));
  const [materials, setMaterials] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);
  const [uploadingField, setUploadingField] = useState(null);
  const [preview, setPreview] = useState(null);

  const s = project.structural;
  const materialsById = Object.fromEntries(materials.map((m) => [m.id, m]));
  const { g, sd } = structTotals(s);
  const { rows: comps, manual, grand } = structPriced(s, materialsById);
  const overheadPct = parseFloat(s.overhead_pct) || 0;
  const overhead = grand * overheadPct / 100;
  const total = grand + overhead;
  const computedSum = comps.reduce((a, c) => a + c.lineTotal, 0);
  const groupSums = {};
  manual.forEach((m) => { groupSums[m.group] = (groupSums[m.group] || 0) + m.lineTotal; });
  const materialTotal = computedSum + (groupSums["Malzeme"] || 0);
  const otherGroups = [...MANUAL_GROUPS.filter((x) => x !== "Malzeme" && groupSums[x]), ...Object.keys(groupSums).filter((x) => !MANUAL_GROUPS.includes(x) && groupSums[x])];

  useEffect(() => { materialsApi.list().then(setMaterials).catch(() => {}); }, []);

  const persist = useCallback(async (p) => {
    setSaving(true);
    try {
      await projectsApi.update(id, {
        name: p.name, category: "structural", system_type: null,
        prepared_by: p.prepared_by, project_date: p.project_date,
        customer_company: p.customer_company || "", customer_contact: p.customer_contact || "",
        kdv_pct: p.kdv_pct ?? 20,
        price_mode: "live", line_items: [], structural: p.structural,
        variant_group: p.variant_group || null, group_name: p.group_name || null,
      });
      setSavedAt(Date.now());
    } catch (e) {
      toast.error("Otomatik kayıt başarısız");
    } finally {
      setSaving(false);
    }
  }, [id]);

  const update = (mutator) => {
    setProject((prev) => {
      const next = mutator({ ...prev });
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persist(next), 700);
      return next;
    });
  };

  const setMeta = (field, value) => update((p) => ({ ...p, [field]: value }));
  const setStruct = (field, value) => update((p) => ({ ...p, structural: { ...p.structural, [field]: value } }));
  const addItem = (field) => update((p) => ({ ...p, structural: { ...p.structural, [field]: [...(p.structural[field] || []), newItem()] } }));
  const setItem = (field, idx, prop, value) => update((p) => {
    const arr = (p.structural[field] || []).slice();
    arr[idx] = { ...arr[idx], [prop]: value };
    return { ...p, structural: { ...p.structural, [field]: arr } };
  });
  const removeItem = (field, idx) => update((p) => {
    const arr = (p.structural[field] || []).slice();
    arr.splice(idx, 1);
    return { ...p, structural: { ...p.structural, [field]: arr } };
  });

  const setComp = (compKey, prop, value) => update((p) => {
    const list = (p.structural.components || []).slice();
    const i = list.findIndex((c) => c.key === compKey);
    if (i === -1) list.push({ key: compKey, material_id: null, enabled: true, [prop]: value });
    else list[i] = { ...list[i], [prop]: value };
    return { ...p, structural: { ...p.structural, components: list } };
  });

  const newManualLine = () => ({ id: crypto.randomUUID(), label: "", quantity: 0, unit: "adet", material_id: null, unit_price: 0, group: "Diğer", enabled: true });
  const addManual = () => update((p) => ({ ...p, structural: { ...p.structural, manual_lines: [...(p.structural.manual_lines || []), newManualLine()] } }));
  const addManualPreset = (label, group, unit) => update((p) => ({ ...p, structural: { ...p.structural, manual_lines: [...(p.structural.manual_lines || []), { id: crypto.randomUUID(), label, quantity: 0, unit, material_id: null, unit_price: 0, group, enabled: true }] } }));
  const setManual = (mid, prop, value) => update((p) => {
    const arr = (p.structural.manual_lines || []).slice();
    const i = arr.findIndex((m) => m.id === mid);
    if (i !== -1) arr[i] = { ...arr[i], [prop]: value };
    return { ...p, structural: { ...p.structural, manual_lines: arr } };
  });
  const removeManual = (mid) => update((p) => ({ ...p, structural: { ...p.structural, manual_lines: (p.structural.manual_lines || []).filter((m) => m.id !== mid) } }));
  const copyComponent = (c) => update((p) => {
    const line = { id: crypto.randomUUID(), label: `${c.label} (kopya)`, quantity: c.quantity, unit: c.unit, material_id: c.material_id || null, unit_price: c.unitPrice || 0, group: "Malzeme", enabled: true };
    return { ...p, structural: { ...p.structural, manual_lines: [...(p.structural.manual_lines || []), line] } };
  });

  const exportCost = () => window.open(projectsApi.exportUrl(id, "cost"), "_blank");
  const openTeklifApp = () => window.open("https://naswoodteklif.figma.site", "_blank");

  const onPhoto = async (field, e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingField(field);
    try {
      const { rows } = await projectsApi.extractImage(files);
      if (!rows || rows.length === 0) { toast.info("Görselde ölçü satırı bulunamadı"); return; }
      const fallback = field === "solid_items" ? "solid" : "glulam";
      setPreview({ rows: rows.map((r) => ({ id: crypto.randomUUID(), width: r.width, height: r.height, length: r.length, qty: r.qty, list: r.type === "solid" ? "solid_items" : r.type === "glulam" ? "glulam_items" : (fallback + "_items") })) });
    } catch (err) {
      toast.error((err && err.response && err.response.data && err.response.data.detail) || "Fotoğraf işlenemedi");
    } finally {
      setUploadingField(null);
    }
  };
  const setPreviewCell = (idx, prop, val) => setPreview((p) => { const rows = p.rows.slice(); rows[idx] = { ...rows[idx], [prop]: val }; return { ...p, rows }; });
  const removePreviewRow = (idx) => setPreview((p) => ({ ...p, rows: p.rows.filter((_, i) => i !== idx) }));
  const confirmPreview = () => {
    const mk = (r) => ({ id: crypto.randomUUID(), width: r.width, height: r.height, length: r.length, qty: r.qty });
    const glu = preview.rows.filter((r) => r.list !== "solid_items").map(mk);
    const sol = preview.rows.filter((r) => r.list === "solid_items").map(mk);
    update((p) => ({ ...p, structural: { ...p.structural,
      glulam_items: [...(p.structural.glulam_items || []), ...glu],
      solid_items: [...(p.structural.solid_items || []), ...sol],
    } }));
    toast.success(`${preview.rows.length} satır eklendi (Glulam: ${glu.length}, Solid: ${sol.length})`);
    setPreview(null);
  };

  const selByKey = Object.fromEntries((s.components || []).map((c) => [c.key, c]));

  const numCell = (val, onChange, testid) => (
    <input type="number" step="0.01" min="0" value={val ?? ""} data-testid={testid}
      onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
      className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-amber-600" />
  );

  const itemTable = (field, title) => {
    const items = s[field] || [];
    let tv = 0, tp = 0;
    return (
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-900">{title}</h3>
          <div className="flex items-center gap-2">
            <label data-testid={`photo-${field}`}
              className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium cursor-pointer transition-colors ${uploadingField === field ? "border-amber-300 bg-amber-50 text-amber-700" : "border-amber-300 bg-amber-50/60 text-amber-800 hover:bg-amber-100"}`}>
              {uploadingField === field ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Okunuyor…</>) : (<><ImagePlus className="w-3.5 h-3.5" /> Fotoğraftan ekle</>)}
              <input type="file" accept="image/*" multiple className="hidden" disabled={uploadingField === field}
                data-testid={`photo-input-${field}`} onChange={(e) => onPhoto(field, e)} />
            </label>
            <button onClick={() => addItem(field)} data-testid={`add-${field}`}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">
              <Plus className="w-3.5 h-3.5" /> Satır ekle
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="text-[11px] uppercase tracking-wider text-slate-400">
              <th className="text-right pb-1 w-20">Genişlik (cm)</th>
              <th className="text-right pb-1 w-20">Yükseklik (cm)</th>
              <th className="text-right pb-1 w-20">Boy (cm)</th>
              <th className="text-right pb-1 w-16">Adet</th>
              <th className="text-right pb-1 w-20">Hacim m³</th>
              <th className="text-right pb-1 w-24">Boya m² (4 yüzey)</th>
              <th className="w-6"></th>
            </tr></thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={7} className="py-3 text-center text-slate-400 text-xs" data-testid={`${field}-empty`}>Kayıt yok. Satır ekleyin veya fotoğraftan çekin.</td></tr>
              ) : items.map((it, idx) => {
                const m = structItemMetrics(it);
                tv += m.volume_m3; tp += m.paint_m2;
                return (
                  <tr key={it.id} className="border-t border-slate-100" data-testid={`${field}-row-${idx}`}>
                    <td className="py-1 px-0.5">{numCell(it.width, (v) => setItem(field, idx, "width", v), `${field}-${idx}-width`)}</td>
                    <td className="py-1 px-0.5">{numCell(it.height, (v) => setItem(field, idx, "height", v), `${field}-${idx}-height`)}</td>
                    <td className="py-1 px-0.5">{numCell(it.length, (v) => setItem(field, idx, "length", v), `${field}-${idx}-length`)}</td>
                    <td className="py-1 px-0.5">{numCell(it.qty, (v) => setItem(field, idx, "qty", v), `${field}-${idx}-qty`)}</td>
                    <td className="py-1 px-1 text-right tabular-nums font-semibold text-amber-900" data-testid={`${field}-${idx}-volume`}>{fmt(m.volume_m3)}</td>
                    <td className="py-1 px-1 text-right tabular-nums text-slate-700" data-testid={`${field}-${idx}-paint`}>{fmt(m.paint_m2)}</td>
                    <td className="py-1 text-center">
                      <button onClick={() => removeItem(field, idx)} data-testid={`${field}-${idx}-delete`} className="p-1 rounded text-red-400 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {items.length > 0 && (
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td colSpan={4} className="py-1.5 px-1 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Toplam</td>
                  <td className="py-1.5 px-1 text-right tabular-nums font-bold text-slate-800" data-testid={`${field}-total-volume`}>{fmt(tv)}</td>
                  <td className="py-1.5 px-1 text-right tabular-nums font-bold text-slate-800" data-testid={`${field}-total-paint`}>{fmt(tp)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto p-4 md:p-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => nav("/")} data-testid="back-btn" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Tüm projeler
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="save-status">
            {saving ? (<><Save className="w-3.5 h-3.5 animate-pulse" /> Kaydediliyor…</>)
              : savedAt ? (<><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Kaydedildi</>) : null}
          </span>
          <button onClick={exportCost} data-testid="export-cost-btn"
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
            <Download className="w-4 h-4" /> Maliyet Tablosu
          </button>
          <button onClick={openTeklifApp} data-testid="open-teklif-app-btn"
            className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 transition-colors">
            <ExternalLink className="w-4 h-4" /> Teklif Hazırla
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
        <div className="grid gap-4 md:grid-cols-4">
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Proje adı</label>
            <input data-testid="header-name-input" value={project.name} onChange={(e) => setMeta("name", e.target.value)}
              className="mt-1 w-full font-heading text-2xl font-extrabold text-slate-900 bg-transparent border-b border-transparent focus:border-amber-500 outline-none" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hazırlayan</label>
            <input data-testid="header-preparedby-input" value={project.prepared_by || ""} onChange={(e) => setMeta("prepared_by", e.target.value)} placeholder="İsim"
              className="mt-1 w-full text-sm text-slate-900 bg-transparent border-b border-slate-200 focus:border-amber-500 outline-none py-1" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tarih</label>
            <input data-testid="header-date-input" type="date" value={project.project_date || ""} onChange={(e) => setMeta("project_date", e.target.value)}
              className="mt-1 w-full text-sm text-slate-900 bg-transparent border-b border-slate-200 focus:border-amber-500 outline-none py-1 tabular-nums" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Müşteri / Firma</label>
            <input data-testid="header-customer-company-input" value={project.customer_company || ""} onChange={(e) => setMeta("customer_company", e.target.value)} placeholder="Müşteri firma / proje adı"
              className="mt-1 w-full text-sm text-slate-900 bg-transparent border-b border-slate-200 focus:border-amber-500 outline-none py-1" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Yetkili Kişi</label>
            <input data-testid="header-customer-contact-input" value={project.customer_contact || ""} onChange={(e) => setMeta("customer_contact", e.target.value)} placeholder="Müşteri yetkilisi"
              className="mt-1 w-full text-sm text-slate-900 bg-transparent border-b border-slate-200 focus:border-amber-500 outline-none py-1" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">KDV %</label>
            <input data-testid="header-kdv-input" type="number" step="1" min="0" value={project.kdv_pct ?? 20} onChange={(e) => setMeta("kdv_pct", e.target.value === "" ? 0 : parseFloat(e.target.value))}
              className="mt-1 w-full text-sm text-slate-900 bg-transparent border-b border-slate-200 focus:border-amber-500 outline-none py-1 tabular-nums" />
          </div>
        </div>
      </div>

      {/* Input lists */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5 space-y-4">
        <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2"><Layers className="w-5 h-5 text-amber-600" /> Girdi Listeleri (cm)</h2>
        <p className="text-xs text-slate-400 -mt-2">Ölçüleri cm girin ya da "Fotoğraftan ekle" ile bir liste/tablo fotoğrafından otomatik çekin. Hacim m³ ve 4 yüzey boya alanı m² otomatik hesaplanır.</p>
        {itemTable("glulam_items", "Girdi Listesi 1 — Glulam")}
        {itemTable("solid_items", "Girdi Listesi 2 — Solid")}
      </div>

      {/* Input totals */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">Girdi Toplamları</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            ["Glulam hacim", g.volume, "m³"],
            ["Glulam boya", g.paint, "m²"],
            ["Glulam yangın gec.", g.paint, "m²"],
            ["Solid hacim", sd.volume, "m³"],
            ["Solid boya", sd.paint, "m²"],
            ["Solid yangın gec.", sd.volume, "m³"],
          ].map(([label, value, unit]) => (
            <div key={label} className="rounded-md bg-slate-50 border border-slate-200 p-3">
              <div className="text-xs text-slate-500">{label}</div>
              <div className="text-lg font-extrabold tabular-nums text-slate-900" data-testid={`total-${label}`}>
                {fmt(value)} <span className="text-xs font-medium text-slate-400">{unit}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cost lines: computed + manual */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-heading text-lg font-bold text-slate-900">Maliyet Kalemleri & Malzeme Seçimi</h2>
          <p className="text-xs text-slate-400 mt-1">Hesaplanan miktarlar için malzeme seçince birim fiyat kütüphaneden gelir. İstemediğiniz kalemi kapatabilir, elle kalem ekleyebilirsiniz.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-slate-100">
                {["Dahil", "Bileşen", "Miktar", "Birim", "Malzeme (kütüphane)", "Birim Fiyat", "Tutar", ""].map((h, i) => (
                  <th key={i} className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 ${i === 2 || (i >= 5 && i <= 6) ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comps.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400 text-sm" data-testid="comps-empty">Girdi listelerine satır ekleyin; hesaplanan maliyet kalemleri burada görünecek.</td></tr>
              )}
              {comps.map((c) => {
                const sel = selByKey[c.key] || {};
                const enabled = sel.enabled !== false;
                const wantUnit = UNIT_MAP[c.unit];
                const opts = materials.filter((m) => m.calc_unit === wantUnit);
                const list = opts.length ? opts : materials;
                return (
                  <tr key={c.key} className={`border-b border-slate-100 ${enabled ? "" : "opacity-40"}`} data-testid={`comp-${c.key}`}>
                    <td className="px-3 py-2 text-center">
                      <input type="checkbox" checked={enabled} data-testid={`comp-${c.key}-enabled`}
                        onChange={(e) => setComp(c.key, "enabled", e.target.checked)} className="w-4 h-4 accent-amber-600" />
                    </td>
                    <td className="px-3 py-2 text-sm font-medium text-slate-900">
                      {c.label}
                      <div className="mt-1 flex gap-1">
                        <input value={sel.wood_species || ""} placeholder="Ahşap cinsi" data-testid={`comp-${c.key}-species`}
                          onChange={(e) => setComp(c.key, "wood_species", e.target.value)}
                          className="w-full rounded border border-slate-200 px-1.5 py-1 text-xs text-slate-500 outline-none focus:ring-2 focus:ring-amber-600" />
                        <input value={sel.quality || ""} placeholder="Kalite" data-testid={`comp-${c.key}-quality`}
                          onChange={(e) => setComp(c.key, "quality", e.target.value)}
                          className="w-20 rounded border border-slate-200 px-1.5 py-1 text-xs text-slate-500 outline-none focus:ring-2 focus:ring-amber-600" />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums font-semibold text-amber-900" data-testid={`comp-${c.key}-qty`}>{fmt(c.quantity)}</td>
                    <td className="px-3 py-2 text-sm text-slate-500">{c.unit}</td>
                    <td className="px-3 py-2">
                      <Select value={sel.material_id || ""} onValueChange={(v) => setComp(c.key, "material_id", v)}>
                        <SelectTrigger data-testid={`comp-${c.key}-material`} className="h-9 text-sm"><SelectValue placeholder="Malzeme seç" /></SelectTrigger>
                        <SelectContent>
                          {list.map((m) => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-1 w-28">
                      {sel.material_id ? (
                        <div className="text-sm text-right tabular-nums text-slate-700 py-1.5">{fmtMoney(c.unitPrice)}</div>
                      ) : (
                        <input type="number" step="0.01" value={sel.unit_price ?? 0} data-testid={`comp-${c.key}-price`}
                          onChange={(e) => setComp(c.key, "unit_price", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                          className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-amber-600" />
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-right tabular-nums font-bold text-slate-900" data-testid={`comp-${c.key}-total`}>{fmtMoney(c.lineTotal)}</td>
                    <td className="px-2 py-2 text-center">
                      <button onClick={() => copyComponent(c)} data-testid={`comp-${c.key}-copy`} title="Maliyet satırı olarak kopyala"
                        className="p-1.5 rounded text-slate-400 hover:bg-amber-50 hover:text-amber-700"><Copy className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}

              {manual.length > 0 && (() => {
                const byGroup = {};
                manual.forEach((m) => { (byGroup[m.group] = byGroup[m.group] || []).push(m); });
                const order = [...MANUAL_GROUPS.filter((x) => byGroup[x]), ...Object.keys(byGroup).filter((x) => !MANUAL_GROUPS.includes(x))];
                return order.map((gg) => {
                  const gl = byGroup[gg];
                  const sub = gl.reduce((a, x) => a + x.lineTotal, 0);
                  return (
                    <Fragment key={gg}>
                      <tr className="bg-slate-100">
                        <td colSpan={6} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-600" data-testid={`manual-group-${gg}`}>» {gg}</td>
                        <td className="px-3 py-1.5 text-right text-xs font-bold tabular-nums text-slate-700" data-testid={`manual-group-${gg}-subtotal`}>{fmtMoney(sub)}</td>
                        <td></td>
                      </tr>
                      {gl.map((m) => (
                        <tr key={m.id} className={`border-b border-slate-100 ${m.enabled ? "" : "opacity-40"}`} data-testid={`manual-${m.id}`}>
                          <td className="px-3 py-2 text-center align-top">
                            <input type="checkbox" checked={m.enabled} data-testid={`manual-${m.id}-enabled`}
                              onChange={(e) => setManual(m.id, "enabled", e.target.checked)} className="w-4 h-4 accent-amber-600" />
                          </td>
                          <td className="px-3 py-1">
                            <input value={m.label} placeholder="Kalem açıklaması" data-testid={`manual-${m.id}-label`}
                              onChange={(e) => setManual(m.id, "label", e.target.value)}
                              className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-amber-600" />
                            <select value={m.group} data-testid={`manual-${m.id}-group`}
                              onChange={(e) => setManual(m.id, "group", e.target.value)}
                              className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-xs text-slate-500 bg-white outline-none focus:ring-2 focus:ring-amber-600">
                              {MANUAL_GROUPS.map((x) => <option key={x} value={x}>{x}</option>)}
                            </select>
                            <div className="mt-1 flex gap-1">
                              <input value={m.wood_species || ""} placeholder="Ahşap cinsi" data-testid={`manual-${m.id}-species`}
                                onChange={(e) => setManual(m.id, "wood_species", e.target.value)}
                                className="w-full rounded border border-slate-200 px-1.5 py-1 text-xs text-slate-500 outline-none focus:ring-2 focus:ring-amber-600" />
                              <input value={m.quality || ""} placeholder="Kalite" data-testid={`manual-${m.id}-quality`}
                                onChange={(e) => setManual(m.id, "quality", e.target.value)}
                                className="w-20 rounded border border-slate-200 px-1.5 py-1 text-xs text-slate-500 outline-none focus:ring-2 focus:ring-amber-600" />
                            </div>
                          </td>
                          <td className="px-3 py-1 w-24 align-top">
                            <input type="number" step="0.01" value={m.quantity} data-testid={`manual-${m.id}-qty`}
                              onChange={(e) => setManual(m.id, "quantity", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                              className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-amber-600" />
                          </td>
                          <td className="px-3 py-1 w-20 align-top">
                            <input value={m.unit} data-testid={`manual-${m.id}-unit`}
                              onChange={(e) => setManual(m.id, "unit", e.target.value)}
                              className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right outline-none focus:ring-2 focus:ring-amber-600" />
                          </td>
                          <td className="px-3 py-1 align-top">
                            <Select value={m.material_id || "none"} onValueChange={(v) => setManual(m.id, "material_id", v === "none" ? null : v)}>
                              <SelectTrigger data-testid={`manual-${m.id}-material`} className="h-9 text-sm"><SelectValue placeholder="Malzeme (opsiyonel)" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— (elle fiyat)</SelectItem>
                                {materials.map((mat) => (<SelectItem key={mat.id} value={mat.id}>{mat.name}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-1 w-28 align-top">
                            {m.material_id ? (
                              <div className="text-sm text-right tabular-nums text-slate-500 py-1.5" data-testid={`manual-${m.id}-price`}>{fmtMoney(m.unitPrice)}</div>
                            ) : (
                              <input type="number" step="0.01" value={m.unitPrice} data-testid={`manual-${m.id}-price`}
                                onChange={(e) => setManual(m.id, "unit_price", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                                className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-amber-600" />
                            )}
                          </td>
                          <td className="px-3 py-2 text-sm text-right tabular-nums font-bold text-slate-900 align-top" data-testid={`manual-${m.id}-total`}>{fmtMoney(m.lineTotal)}</td>
                          <td className="px-2 py-2 text-center align-top">
                            <button onClick={() => removeManual(m.id)} data-testid={`manual-${m.id}-delete`} className="p-1.5 rounded text-red-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                });
              })()}

              <tr>
                <td colSpan={8} className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={addManual} data-testid="add-manual-line"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                      <Plus className="w-4 h-4" /> Manuel maliyet satırı ekle
                    </button>
                    <button onClick={() => addManualPreset("", "Malzeme", "adet")} data-testid="add-malzeme"
                      className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100">
                      <Plus className="w-4 h-4" /> Manuel malzeme satırı ekle
                    </button>
                    <button onClick={() => addManualPreset("Konaklama", "İşçilik", "gece")} data-testid="add-konaklama"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                      <Plus className="w-4 h-4" /> Konaklama (İşçilik)
                    </button>
                    <button onClick={() => addManualPreset("Yeme-içme", "İşçilik", "kişi-gün")} data-testid="add-yemeicme"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                      <Plus className="w-4 h-4" /> Yeme-içme (İşçilik)
                    </button>
                    <button onClick={() => addManualPreset("Yevmiye", "İşçilik", "yevmiye")} data-testid="add-yevmiye"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                      <Plus className="w-4 h-4" /> Yevmiye (İşçilik)
                    </button>
                    <button onClick={() => addManualPreset("Vinç", "İşçilik", "gün")} data-testid="add-vinc"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                      <Plus className="w-4 h-4" /> Vinç (İşçilik)
                    </button>
                    <button onClick={() => addManualPreset("Nakliye", "Nakliye", "sefer")} data-testid="add-nakliye"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                      <Plus className="w-4 h-4" /> Nakliye
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200">
                <td colSpan={7} className="px-3 py-2 text-right text-sm font-semibold text-slate-600">Malzeme Maliyeti</td>
                <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-800 whitespace-nowrap" data-testid="material-total">{fmtMoney(materialTotal)}</td>
              </tr>
              {otherGroups.map((gg) => (
                <tr key={gg}>
                  <td colSpan={7} className="px-3 py-2 text-right text-sm font-semibold text-slate-600">{gg === "İşçilik" ? "İşçilik Maliyeti" : `${gg} Maliyeti`}</td>
                  <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-800 whitespace-nowrap" data-testid={`group-total-${gg}`}>{fmtMoney(groupSums[gg])}</td>
                </tr>
              ))}
              <tr className="border-t border-slate-200">
                <td colSpan={7} className="px-3 py-2 text-right text-sm font-semibold text-slate-600">Ara Toplam</td>
                <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-800 whitespace-nowrap" data-testid="subtotal">{fmtMoney(grand)}</td>
              </tr>
              <tr>
                <td colSpan={7} className="px-3 py-2 text-right text-sm font-semibold text-slate-600">
                  <span className="inline-flex items-center gap-2 justify-end">
                    Genel Gider
                    <input type="number" step="0.1" value={s.overhead_pct ?? 0} data-testid="overhead-pct"
                      onChange={(e) => setStruct("overhead_pct", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                      className="w-20 rounded border border-slate-200 px-2 py-1 text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-amber-600" />
                    %
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-800 whitespace-nowrap" data-testid="overhead-amount">{fmtMoney(overhead)}</td>
              </tr>
              <tr className="bg-amber-50 border-t-2 border-amber-200">
                <td colSpan={7} className="px-3 py-3 text-right text-sm font-bold uppercase tracking-wider text-slate-700">Toplam Maliyet</td>
                <td className="px-3 py-3 text-right text-lg font-extrabold tabular-nums text-amber-900 whitespace-nowrap" data-testid="grand-total">{fmtMoney(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="photo-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Fotoğraftan çıkarılan ölçüler</DialogTitle>
            <DialogDescription>Değerleri kontrol edip düzeltin (cm) ve her satırın hangi listeye (Glulam/Solid) gideceğini onaylayın. Birden fazla fotoğraf yükleyebilirsiniz.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-wider text-slate-400">
                <th className="text-right pb-1">Genişlik (cm)</th>
                <th className="text-right pb-1">Yükseklik (cm)</th>
                <th className="text-right pb-1">Boy (cm)</th>
                <th className="text-right pb-1">Adet</th>
                <th className="text-left pb-1 pl-2">Liste</th>
                <th className="w-6"></th>
              </tr></thead>
              <tbody>
                {preview && preview.rows.length === 0 && (
                  <tr><td colSpan={6} className="py-4 text-center text-slate-400 text-xs">Satır kalmadı.</td></tr>
                )}
                {preview && preview.rows.map((r, idx) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`preview-row-${idx}`}>
                    {["width", "height", "length", "qty"].map((prop) => (
                      <td key={prop} className="py-1 px-0.5">
                        <input type="number" step="0.01" min="0" value={r[prop] ?? ""} data-testid={`preview-${idx}-${prop}`}
                          onChange={(e) => setPreviewCell(idx, prop, e.target.value === "" ? 0 : parseFloat(e.target.value))}
                          className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-amber-600" />
                      </td>
                    ))}
                    <td className="py-1 pl-2">
                      <select value={r.list} data-testid={`preview-${idx}-list`}
                        onChange={(e) => setPreviewCell(idx, "list", e.target.value)}
                        className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm bg-white outline-none focus:ring-2 focus:ring-amber-600">
                        <option value="glulam_items">Glulam</option>
                        <option value="solid_items">Solid</option>
                      </select>
                    </td>
                    <td className="py-1 text-center">
                      <button onClick={() => removePreviewRow(idx)} data-testid={`preview-${idx}-delete`} className="p-1 rounded text-red-400 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <button onClick={() => setPreview(null)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Vazgeç</button>
            <button onClick={confirmPreview} data-testid="preview-confirm-btn" disabled={!preview || preview.rows.length === 0}
              className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-40">Listeye ekle</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
